import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MessageSquare,
  Zap,
  Brain,
  Clock,
  ArrowRight,
  Sparkles,
  TrendingUp,
  Activity,
  Target,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { useThemeStore } from '../store/theme';
import { AI_EMPLOYEES } from '../data/employees';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface DashboardStats {
  messages_today: number;
  tasks_completed: number;
  brain_documents: number;
  time_saved_hours: number;
}

interface RecentConversation {
  id: number;
  employee_id: string;
  title: string;
  last_message_at: string;
}

interface OnboardingStep {
  title: string;
  completed: boolean;
}

interface OnboardingData {
  steps: OnboardingStep[];
  completed: number;
  total: number;
}

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.4 } },
};

// Helper to get employee info
const getEmployeeInfo = (employeeId: string) => {
  const employee = AI_EMPLOYEES.find(e => e.id === employeeId);
  return employee || { name: 'AI', avatar: '🤖', gradient: 'from-gray-500 to-gray-600' };
};

// Helper to format relative time
// SQLite stores dates in UTC format like "YYYY-MM-DD HH:MM:SS"
// We need to append 'Z' to treat it as UTC
const formatRelativeTime = (dateString: string) => {
  if (!dateString) return 'just now';

  // Handle SQLite datetime format - append Z to indicate UTC
  const utcDateString = dateString.includes('T') || dateString.includes('Z')
    ? dateString
    : dateString.replace(' ', 'T') + 'Z';

  const date = new Date(utcDateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  // Handle future dates or invalid dates
  if (diffMs < 0 || isNaN(diffMs)) return 'just now';

  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString();
};

export default function Dashboard() {
  const { user, token } = useAuthStore();
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentConversations, setRecentConversations] = useState<RecentConversation[]>([]);
  const [onboarding, setOnboarding] = useState<OnboardingData | null>(null);
  const [loading, setLoading] = useState(true);

  // Get first 6 employees for quick access
  const quickAccessEmployees = AI_EMPLOYEES.slice(0, 6);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!token) return;

      try {
        const headers = { Authorization: `Bearer ${token}` };

        // Fetch all dashboard data in parallel
        const [statsRes, conversationsRes, onboardingRes] = await Promise.all([
          fetch(`${API_URL}/dashboard/stats`, { headers }),
          fetch(`${API_URL}/dashboard/recent-conversations?limit=3`, { headers }),
          fetch(`${API_URL}/dashboard/onboarding`, { headers }),
        ]);

        if (statsRes.ok) {
          const statsData = await statsRes.json() as { success: boolean; data: DashboardStats };
          if (statsData.success) setStats(statsData.data);
        }

        if (conversationsRes.ok) {
          const convData = await conversationsRes.json() as { success: boolean; data: RecentConversation[] };
          if (convData.success) setRecentConversations(convData.data);
        }

        if (onboardingRes.ok) {
          const onboardingData = await onboardingRes.json() as { success: boolean; data: OnboardingData };
          if (onboardingData.success) setOnboarding(onboardingData.data);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [token]);

  const stepDescriptions: Record<string, string> = {
    'Chat with an AI Employee': 'Start a conversation to get help with any task',
    'Train your Brain AI': 'Upload documents to personalize responses',
    'Connect an Integration': 'Link your favorite tools for automation',
    'Create an Automation': 'Set up tasks that run automatically',
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="space-y-8"
    >
      {/* Welcome Header with Gradient Background */}
      <motion.div variants={fadeInUp} className="relative overflow-hidden rounded-3xl">
        <div className={`absolute inset-0 blur-3xl ${isDark ? 'bg-gradient-to-br from-purple-600/20 via-cyan-500/10 to-pink-500/20' : 'bg-gradient-to-br from-purple-600/10 via-cyan-500/5 to-pink-500/10'}`} />
        <div className="relative p-6 lg:p-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <motion.h1
                className={`text-3xl lg:text-4xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                Welcome back, {user?.name?.split(' ')[0] || 'there'}!
              </motion.h1>
              <motion.p
                className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                Your AI team is ready to help. What would you like to accomplish today?
              </motion.p>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, type: "spring" }}
            >
              <Link
                to="/dashboard/employees"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl font-semibold text-white shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 hover:scale-105 transition-all"
              >
                <Sparkles className="w-5 h-5" />
                Start New Chat
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Stats Cards with Glass Morphism */}
      <motion.div
        variants={staggerContainer}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
      >
        {[
          {
            label: 'Messages Today',
            value: loading ? '...' : String(stats?.messages_today || 0),
            change: 'Today',
            icon: MessageSquare,
            gradient: 'from-purple-500 to-violet-600',
            shadowColor: 'shadow-purple-500/20',
          },
          {
            label: 'Tasks Completed',
            value: loading ? '...' : String(stats?.tasks_completed || 0),
            change: 'Total',
            icon: Zap,
            gradient: 'from-cyan-500 to-blue-600',
            shadowColor: 'shadow-cyan-500/20',
          },
          {
            label: 'Brain Documents',
            value: loading ? '...' : String(stats?.brain_documents || 0),
            change: 'Uploaded',
            icon: Brain,
            gradient: 'from-green-500 to-emerald-600',
            shadowColor: 'shadow-green-500/20',
          },
          {
            label: 'Time Saved',
            value: loading ? '...' : `${stats?.time_saved_hours || 0}h`,
            change: 'Estimated',
            icon: Clock,
            gradient: 'from-orange-500 to-amber-600',
            shadowColor: 'shadow-orange-500/20',
          },
        ].map((stat) => (
          <motion.div
            key={stat.label}
            variants={scaleIn}
            whileHover={{ y: -5, scale: 1.02 }}
            className={`group relative p-5 rounded-2xl backdrop-blur-sm border transition-all duration-300 ${isDark ? 'bg-dark-800/50 border-white/5 hover:border-white/10' : 'bg-white border-slate-200 hover:border-slate-300 shadow-sm'}`}
          >
            {/* Hover glow effect */}
            <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${stat.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />

            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.gradient} flex items-center justify-center shadow-lg ${stat.shadowColor}`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${isDark ? 'text-gray-400 bg-dark-700' : 'text-slate-500 bg-slate-100'}`}>
                  {stat.change}
                </span>
              </div>
              <p className={`text-3xl font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {loading ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  stat.value
                )}
              </p>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{stat.label}</p>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Quick Actions with Animated Cards */}
      <motion.div variants={fadeInUp}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Quick Actions</h2>
            <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>Get started with popular tasks</p>
          </div>
          <Link
            to="/dashboard/employees"
            className="text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1 group"
          >
            View all
            <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            {
              title: 'Create Social Post',
              description: 'Generate engaging content for any platform',
              employee: 'Aria',
              avatar: '🎀',
              gradient: 'from-pink-500 to-rose-500',
            },
            {
              title: 'Draft Email Campaign',
              description: 'Build high-converting email sequences',
              employee: 'Echo',
              avatar: '📧',
              gradient: 'from-orange-500 to-amber-500',
            },
            {
              title: 'Analyze Data',
              description: 'Get insights from your business data',
              employee: 'Atlas',
              avatar: '📊',
              gradient: 'from-violet-500 to-purple-500',
            },
          ].map((action) => (
            <motion.div
              key={action.title}
              variants={fadeInUp}
              whileHover={{ y: -5 }}
            >
              <Link
                to={`/dashboard/employees/${action.employee.toLowerCase()}`}
                className={`block p-5 rounded-2xl backdrop-blur-sm border transition-all duration-300 group ${isDark ? 'bg-dark-800/50 border-white/5 hover:border-white/20' : 'bg-white border-slate-200 hover:border-slate-300 shadow-sm'}`}
              >
                <div className="flex items-start gap-4">
                  <motion.div
                    className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${action.gradient} flex items-center justify-center flex-shrink-0 shadow-lg`}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <span className="text-2xl">{action.avatar}</span>
                  </motion.div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`font-semibold mb-1 group-hover:text-purple-400 transition-colors flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {action.title}
                      <ArrowRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                    </h3>
                    <p className={`text-sm line-clamp-2 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                      {action.description}
                    </p>
                    <p className="text-xs text-purple-400 mt-2">with {action.employee}</p>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* AI Employees Grid with Emoji Avatars */}
      <motion.div variants={fadeInUp}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Your AI Team</h2>
            <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>15 specialized employees ready to help</p>
          </div>
          <Link
            to="/dashboard/employees"
            className="text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1 group"
          >
            See all employees
            <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {quickAccessEmployees.map((employee) => (
            <motion.div
              key={employee.id}
              variants={scaleIn}
              whileHover={{ y: -8 }}
            >
              <Link
                to={`/dashboard/employees/${employee.id}`}
                className={`block p-4 rounded-2xl backdrop-blur-sm border text-center group transition-all duration-300 ${isDark ? 'bg-dark-800/50 border-white/5 hover:border-white/20' : 'bg-white border-slate-200 hover:border-slate-300 shadow-sm'}`}
              >
                <motion.div
                  className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${employee.gradient} flex items-center justify-center mx-auto mb-3 shadow-lg`}
                  whileHover={{ scale: 1.15, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 400 }}
                >
                  <span className="text-2xl">{employee.avatar}</span>
                </motion.div>
                <h3 className={`font-semibold mb-1 group-hover:text-purple-400 transition-colors ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {employee.name}
                </h3>
                <p className={`text-xs line-clamp-1 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                  {employee.role}
                </p>
                {employee.isNew && (
                  <span className="inline-block mt-2 px-2 py-0.5 text-xs bg-green-500/20 text-green-400 rounded-full">
                    NEW
                  </span>
                )}
              </Link>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Recent Activity & Getting Started */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Conversations */}
        <motion.div variants={fadeInUp}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-lg font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <Activity className="w-5 h-5 text-purple-400" />
              Recent Conversations
            </h2>
          </div>
          <div className={`p-5 rounded-2xl backdrop-blur-sm border ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
              </div>
            ) : recentConversations.length === 0 ? (
              <div className={`text-center py-8 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No conversations yet</p>
                <Link to="/dashboard/employees" className="text-purple-400 text-sm mt-2 block">
                  Start your first chat →
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {recentConversations.map((conv) => {
                  const employee = getEmployeeInfo(conv.employee_id);
                  return (
                    <motion.div
                      key={conv.id}
                      whileHover={{ x: 5 }}
                    >
                      <Link
                        to={`/dashboard/employees/${conv.employee_id}`}
                        className={`flex items-center gap-4 p-3 rounded-xl transition-colors cursor-pointer group ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}
                      >
                        <div
                          className={`w-11 h-11 rounded-xl bg-gradient-to-br ${employee.gradient} flex items-center justify-center flex-shrink-0 shadow-lg`}
                        >
                          <span className="text-lg">{employee.avatar}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate group-hover:text-purple-400 transition-colors ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            {conv.title}
                          </p>
                          <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                            with {employee.name} • {formatRelativeTime(conv.last_message_at)}
                          </p>
                        </div>
                        <ChevronRight className={`w-4 h-4 group-hover:text-purple-400 group-hover:translate-x-1 transition-all ${isDark ? 'text-gray-600' : 'text-slate-400'}`} />
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            )}

            <Link
              to="/dashboard/employees"
              className={`mt-4 block text-center text-sm text-purple-400 hover:text-purple-300 py-2 rounded-xl transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}
            >
              View all conversations
            </Link>
          </div>
        </motion.div>

        {/* Getting Started */}
        <motion.div variants={fadeInUp}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-lg font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <Target className="w-5 h-5 text-cyan-400" />
              Getting Started
            </h2>
          </div>
          <div className={`p-5 rounded-2xl backdrop-blur-sm border ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {(onboarding?.steps || []).map((step, index) => (
                    <motion.div
                      key={step.title}
                      whileHover={{ x: 5 }}
                      className={`flex items-start gap-4 p-3 rounded-xl transition-colors cursor-pointer group ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}
                    >
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          step.completed
                            ? 'bg-green-500/20 text-green-400'
                            : isDark ? 'bg-dark-700 text-gray-500' : 'bg-slate-100 text-slate-500'
                        }`}
                      >
                        {step.completed ? (
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        ) : (
                          <span className="text-xs font-bold">{index + 1}</span>
                        )}
                      </div>
                      <div className="flex-1">
                        <p
                          className={`text-sm font-medium ${
                            step.completed ? (isDark ? 'text-gray-500' : 'text-slate-400') + ' line-through' : (isDark ? 'text-white' : 'text-slate-900') + ' group-hover:text-purple-400 transition-colors'
                          }`}
                        >
                          {step.title}
                        </p>
                        <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                          {stepDescriptions[step.title] || ''}
                        </p>
                      </div>
                      {!step.completed && (
                        <ChevronRight className={`w-4 h-4 group-hover:text-purple-400 group-hover:translate-x-1 transition-all mt-1 ${isDark ? 'text-gray-600' : 'text-slate-400'}`} />
                      )}
                    </motion.div>
                  ))}
                </div>

                {/* Progress indicator */}
                <div className={`mt-4 pt-4 border-t ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
                  <div className={`flex items-center justify-between text-xs mb-2 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                    <span>Progress</span>
                    <span>{onboarding?.completed || 0} of {onboarding?.total || 4} completed</span>
                  </div>
                  <div className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-dark-700' : 'bg-slate-100'}`}>
                    <motion.div
                      className="h-full bg-gradient-to-r from-purple-500 to-cyan-500 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${((onboarding?.completed || 0) / (onboarding?.total || 4)) * 100}%` }}
                      transition={{ duration: 1, delay: 0.5 }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </div>

    </motion.div>
  );
}
